import { makeStyles, Tree, TreeItem, TreeItemLayout } from "@fluentui/react-components";
import { FolderRegular } from "@fluentui/react-icons";
import { useEffect, useState } from "react";

import "./CiTypesStyles.css";

// Components
import CiTypeImage from "./CiTypeImage";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import Spinner from "../../../shared/components/Spinner";

// Business logic
import { useTranslation } from "react-i18next";
import { useSelectedCiTypeContext } from "../context/selectedCiTypeContext";
import { useItemQuery } from "../../../shared/hooks/useItemQuery";
import { readCiTypes } from "../../citypes/services/ciTypesApi";

const useStyles = makeStyles({
  container: {
    height: "100%", // adjust based on layout
    display: "flex",
    flexDirection: "column",
    padding: "8px",
    boxSizing: "border-box",
    overflowY: "auto",
    overflowX: "hidden",
  },
  item: {
    flex: "1" /* equal width */,
    padding: "10px",
    textAlign: "center",
  },
  selectedItem: {
    backgroundColor: "#e0f3ff",
    borderLeft: "3px solid #0078d4",
  },
});

function List({ items, parent_id = null, selectedItem, onSelectItem, hiddenItemId = null }) {
  const styles = useStyles();

  return items
    .filter((item) => item.parent_id === parent_id && item.id !== hiddenItemId)
    .map((ciType) => {
      var childsCount = items.filter((item) => item.parent_id === ciType.id && item.id !== hiddenItemId).length;
      ciType.childs = childsCount > 0 ? true : false;
      return (
        <TreeItem
          key={ciType.id}
          itemType={childsCount > 0 ? "branch" : "leaf"}
          value={ciType.id}
          onClick={() => onSelectItem(ciType)}
        >
          <TreeItemLayout
            className={ciType.id === selectedItem?.id ? styles.selectedItem : undefined}
            iconBefore={ciType.icon_id ? <CiTypeImage ciType={ciType} height={24} /> : <FolderRegular />}
          >
            {ciType.name}
            {childsCount > 0 && ` (${childsCount})`}
          </TreeItemLayout>
          <Tree>
            <List
              items={items}
              parent_id={ciType.id}
              selectedItem={selectedItem}
              onSelectItem={onSelectItem}
              hiddenItemId={hiddenItemId}
            />
          </Tree>
        </TreeItem>
      );
    });
}

function CiTypesTree({ selectedCiType, setSelectedCiType, showRootItem = false, hiddenItemId = null } = {}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { value: contextSelectedCiType, setValue: contextSetSelectedCiType } = useSelectedCiTypeContext();
  const effectiveSelectedCiType = selectedCiType === undefined ? contextSelectedCiType : selectedCiType;
  const effectiveSetSelectedCiType = setSelectedCiType === undefined ? contextSetSelectedCiType : setSelectedCiType;

  // Errors
  const [errors, setErrors] = useState([]);

  const { data = [], isFetching, error } = useItemQuery([`citypes`], () => readCiTypes({}), true);

  // Effects
  useEffect(() => {
    const newErrors = [];
    if (error) {
      newErrors.push({ message: error?.message || t("generic.error"), type: "error", details: error });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [error, t]);

  if (isFetching) return <Spinner label={t("generic.loading")} />;

  return (
    <div className={styles.container}>
      <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
      <Tree aria-label="CI Types Tree" density="compact">
        {showRootItem && (
          <TreeItem itemType="leaf" value="__root__" onClick={() => effectiveSetSelectedCiType?.(null)}>
            <TreeItemLayout
              className={effectiveSelectedCiType == null ? styles.selectedItem : undefined}
              iconBefore={<FolderRegular />}
            >
              <b>{t("generic.root", { defaultValue: "Root" })}</b>
            </TreeItemLayout>
          </TreeItem>
        )}
        <List
          items={data}
          selectedItem={effectiveSelectedCiType}
          onSelectItem={effectiveSetSelectedCiType}
          hiddenItemId={hiddenItemId}
        />
      </Tree>
    </div>
  );
}

export default CiTypesTree;
